import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, Download, Award } from "lucide-react";
import { buildCertificatePdf, downloadCertificate } from "@/components/common/CertificateDownload";

interface InlineCertificateViewerProps {
  fullName: string;
  certId: string;
  date: string;
}

const InlineCertificateViewer = ({ fullName, certId, date }: InlineCertificateViewerProps) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let revoked = false;
    setLoading(true);
    setError(false);
    buildCertificatePdf(fullName, certId, date)
      .then((bytes) => {
        if (revoked) return;
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
        setPdfUrl(URL.createObjectURL(blob));
      })
      .catch((err) => {
        if (!revoked) setError(true);
      })
      .finally(() => { if (!revoked) setLoading(false); });
    return () => {
      revoked = true;
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [open, fullName, certId, date]);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        title="View Certificate"
        className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
        onClick={() => setOpen(true)}
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" />
              Certificate — {fullName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 rounded-lg overflow-hidden border bg-white flex items-center justify-center">
            {loading ? (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : error ? (
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Award className="h-12 w-12 text-primary/30" />
                <p className="text-sm">Failed to generate certificate preview.</p>
                <p className="text-xs">You can still download the certificate below.</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0&view=Fit`}
                className="w-full h-full"
                title={`Certificate for ${fullName}`}
              />
            ) : null}
          </div>
          <DialogFooter>
            <Button
              className="gradient-warm text-primary-foreground"
              onClick={() => downloadCertificate(fullName, certId, date)}
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InlineCertificateViewer;
